'use client';

/**
 * Billing mutations + queries.  All endpoints documented in
 * docs/registered-users/subscribe.json + billing.json.
 *
 * IMPORTANT — `useSubscribe` does NOT redirect.  It returns the
 * `checkout_url` and lets the caller navigate (window.location or a
 * new tab).  The component layer owns UX decisions.
 *
 * After a successful checkout-and-return, poll /auth/me every 2-3 s for
 * up to ~30 s until `org.plan` flips (subscribe.json:post_subscribe_flow).
 * That poll is driven from the billing page, not this hook.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch, useApi } from '@/lib/api';
import { USERS_ME_QUERY_KEY } from '@/lib/auth';

import type {
  BackendPlan,
  CancelResponse,
  ConfirmPaymentParams,
  ConfirmPaymentResponse,
  FoundingMemberClaim,
  FoundingMemberStatus,
  PortalResponse,
  SubscribeParams,
  SubscribeResponse,
} from './types';

export const FOUNDING_MEMBER_QUERY_KEY = ['billing', 'founding-member', 'status'] as const;
export const BILLING_PLANS_QUERY_KEY = ['billing', 'plans'] as const;

/**
 * GET /api/v1/billing/plans — public pricing data.  Skips auth so the
 * marketing pricing page can call this without a JWT (subscribe.json:10).
 * Cached for 5 minutes per the doc's caching note.
 */
export function useBillingPlans() {
  return useQuery<readonly BackendPlan[], Error>({
    queryKey: BILLING_PLANS_QUERY_KEY,
    queryFn: ({ signal }) =>
      apiFetch<readonly BackendPlan[]>('/billing/plans', { signal, skipAuth: true }),
    staleTime: 5 * 60_000,
  });
}

/**
 * POST /api/v1/billing/subscribe — initiate a checkout for a self-serve
 * plan.  Per subscribe.json the body is `{ plan_name, gateway, success_url?,
 * cancel_url? }`; success_url / cancel_url default to the documented
 * /billing?success=1 / /billing?cancelled=1 on the backend.
 */
export function useSubscribe() {
  const api = useApi();
  return useMutation<SubscribeResponse, Error, SubscribeParams>({
    mutationFn: (body) => api<SubscribeResponse>('/billing/subscribe', { method: 'POST', body }),
  });
}

/**
 * POST /api/v1/billing/confirm-payment — fallback used after the embedded
 * Razorpay Checkout modal closes. Polls Razorpay server-side and applies
 * the plan change + emails the receipt, in case the webhook is slow or
 * (in local dev) unreachable. Idempotent vs the webhook.
 */
export function useConfirmPayment() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<ConfirmPaymentResponse, Error, ConfirmPaymentParams>({
    mutationFn: (body) =>
      api<ConfirmPaymentResponse>('/billing/confirm-payment', { method: 'POST', body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USERS_ME_QUERY_KEY });
    },
  });
}

export function usePortal() {
  const api = useApi();
  return useMutation<PortalResponse, Error, void>({
    mutationFn: () => api<PortalResponse>('/billing/portal', { method: 'POST' }),
  });
}

export function useCancelSubscription() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<CancelResponse, Error, void>({
    mutationFn: () => api<CancelResponse>('/billing/cancel', { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USERS_ME_QUERY_KEY });
    },
  });
}

export function useFoundingMemberStatus(options?: { enabled?: boolean }) {
  const api = useApi();
  return useQuery<FoundingMemberStatus, Error>({
    queryKey: FOUNDING_MEMBER_QUERY_KEY,
    queryFn: ({ signal }) =>
      api<FoundingMemberStatus>('/billing/founding-member/status', { signal }),
    enabled: options?.enabled ?? true,
    staleTime: 60_000,
  });
}

export function useFoundingMemberClaim() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<FoundingMemberClaim, Error, { gateway: 'stripe' | 'razorpay' }>({
    mutationFn: (body) =>
      api<FoundingMemberClaim>('/billing/founding-member/claim', { method: 'POST', body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FOUNDING_MEMBER_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: USERS_ME_QUERY_KEY });
    },
  });
}
